import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import { getAvatarColors } from '../utils/avatars';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

const ROLES = ['admin', 'manager', 'mr', 'bd', 'scientist', 'rnd_head', 'principal_scientist', 'sales', 'finance', 'procurement', 'software', 'director', 'ceo'];
const DEPARTMENTS = ['Research & Development', 'Quality Control', 'Quality Assurance', 'Regulatory Affairs',
  'Sales & Marketing', 'Business Development', 'Finance', 'Human Resources', 'Operations', 'IT', 'Administration', 'Logistics'];
const ROLE_LABELS = {
  admin: 'Admin', manager: 'Manager', mr: 'MR', bd: 'BD', scientist: 'Scientist',
  rnd_head: 'R&D Head', principal_scientist: 'Principal Scientist',
  sales: 'Sales', finance: 'Finance', procurement: 'Procurement',
  software: 'Software', director: 'Director', ceo: 'CEO',
};
const roleColors = {
  admin: 'error', manager: 'warning', mr: 'info', bd: 'success', scientist: 'primary',
  rnd_head: 'primary', principal_scientist: 'primary', sales: 'success',
  finance: 'warning', procurement: 'info', software: 'info', director: 'error', ceo: 'error',
};
const roleLabel = (r) => ROLE_LABELS[r] || (r ? r.charAt(0).toUpperCase() + r.slice(1) : '—');
const roleEmoji = {
  admin: '🔑', manager: '👔', mr: '🚗', bd: '💼', scientist: '🔬',
  rnd_head: '🧪', principal_scientist: '🔬', sales: '📈',
  finance: '💰', procurement: '📦', software: '💻', director: '🏛️', ceo: '👑',
};

const defaultForm = { employee_id: '', name: '', email: '', password: '', role: 'mr', territory: '', phone: '', username: '', department: '', reports_to: '', allowed_pages: null };
const defaultPersonaForm = { name: '', description: '', role: 'mr', roles: [], department: '', territory: '', allowed_pages: null };

// ─── Page definitions for access control ────────────────────────────────────
const PAGE_SECTIONS = [
  {
    section: 'Commercial & Sales',
    pages: [
      { key: 'clients', label: 'Clients', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'partners', label: 'Partners', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'customers', label: 'Customers', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'catalog', label: 'Catalog', defaultRoles: null },
      { key: 'inquiries', label: 'Inquiries', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'quotes', label: 'Quotes', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'orders', label: 'Orders', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'visits', label: 'Visits', defaultRoles: ['admin', 'manager', 'bd', 'mr'] },
      { key: 'activities', label: 'Activities', defaultRoles: ['admin', 'manager', 'mr', 'bd'] },
      { key: 'samples', label: 'Samples', defaultRoles: ['admin', 'manager', 'mr'] },
      { key: 'competitors', label: 'Competitors', defaultRoles: ['admin', 'manager', 'mr'] },
    ],
  },
  {
    section: 'Projects & Science',
    pages: [
      { key: 'projects', label: 'Projects', defaultRoles: ['admin', 'manager', 'bd', 'scientist'] },
      { key: 'coa', label: 'COA', defaultRoles: ['admin', 'manager', 'bd', 'scientist'] },
      { key: 'chem-structure', label: 'Chem Structure', defaultRoles: ['admin', 'manager', 'bd', 'scientist'] },
      { key: 'service-requests', label: 'Service Requests', defaultRoles: null },
    ],
  },
  {
    section: 'Finance & Documents',
    pages: [
      { key: 'tax-invoice-orders', label: 'Tax Invoice Orders', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'proforma-invoice-orders', label: 'Proforma Invoice Orders', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'purchase-orders', label: 'Purchase Orders', defaultRoles: ['admin', 'manager', 'bd'] },
      { key: 'accounting', label: 'Accounting', defaultRoles: ['admin', 'manager'] },
      { key: 'receivables', label: 'Receivables', defaultRoles: ['admin', 'manager'] },
      { key: 'balance-sheet', label: 'Balance Sheet', defaultRoles: ['admin', 'manager'] },
      { key: 'gst-report', label: 'GST Report', defaultRoles: ['admin', 'manager'] },
    ],
  },
  {
    section: 'Ops & HR',
    pages: [
      { key: 'attendance', label: 'Attendance', defaultRoles: null },
      { key: 'hr', label: 'HR', defaultRoles: ['admin', 'manager'] },
      { key: 'analytics', label: 'Analytics', defaultRoles: ['admin', 'manager'] },
      { key: 'license-expiry', label: 'License Expiry', defaultRoles: ['admin', 'manager', 'mr'] },
      { key: 'tva', label: 'TVA (Alerts)', defaultRoles: ['admin', 'manager', 'mr', 'bd'] },
      { key: 'capa', label: 'CAPA', defaultRoles: ['admin', 'manager'] },
    ],
  },
  {
    section: 'Administration',
    pages: [
      { key: 'users', label: 'User Management', defaultRoles: ['admin', 'manager'] },
      { key: 'territories', label: 'Territories', defaultRoles: ['admin', 'manager'] },
      { key: 'web-inquiries', label: 'Web Inquiries', defaultRoles: ['admin', 'manager'] },
      { key: 'audit-logs', label: 'Audit Logs', defaultRoles: ['admin', 'manager'] },
      { key: 'data-export', label: 'Data Export', defaultRoles: ['admin', 'manager'] },
      { key: 'email-templates', label: 'Email Templates', defaultRoles: ['admin', 'manager'] },
      { key: 'company-settings', label: 'Company Settings', defaultRoles: ['admin'] },
      { key: 'webhooks', label: 'Webhooks', defaultRoles: ['admin'] },
    ],
  },
];
const ALL_PAGES = PAGE_SECTIONS.flatMap(s => s.pages);

// Given one or more roles, return the union of default page keys
const defaultPagesForRole = (role) =>
  ALL_PAGES.filter(p => !p.defaultRoles || p.defaultRoles.includes(role)).map(p => p.key);
const defaultPagesForRoles = (rolesArr) => {
  const set = Array.isArray(rolesArr) && rolesArr.length ? rolesArr : [rolesArr].filter(Boolean);
  return ALL_PAGES.filter(p => !p.defaultRoles || p.defaultRoles.some(r => set.includes(r))).map(p => p.key);
};

// ─── Persona Permission Editor ───────────────────────────────────────────────
// Controlled: permissions = [{ page, access_level, visibility, expires_after_days }]
// onChange(newArray) called on every change. Parent owns load/save.
function PersonaPermissionEditor({ permissions, onChange }) {
  const permMap = Object.fromEntries((permissions || []).map(p => [p.page, p]));

  const togglePage = (key) => {
    if (permMap[key]) {
      onChange(permissions.filter(p => p.page !== key));
    } else {
      onChange([...permissions, { page: key, access_level: 'read_write', visibility: 'own', expires_after_days: null }]);
    }
  };

  const updatePerm = (key, field, value) =>
    onChange(permissions.map(p => p.page === key ? { ...p, [field]: value } : p));

  const colHdr = { fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.6px' };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 96px 76px 90px', gap: 6, padding: '0 10px', marginBottom: 4 }}>
        <span style={colHdr}>Page</span>
        <span style={colHdr}>Access</span>
        <span style={colHdr}>Scope</span>
        <span style={colHdr}>Expires (days)</span>
      </div>
      {PAGE_SECTIONS.map(sec => (
        <div key={sec.section} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', marginBottom: 5, paddingLeft: 10 }}>
            {sec.section}
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            {sec.pages.map((p, idx) => {
              const perm    = permMap[p.key];
              const granted = !!perm;
              const isLast  = idx === sec.pages.length - 1;
              return (
                <div key={p.key} style={{
                  display: 'grid', gridTemplateColumns: '1fr 96px 76px 90px',
                  gap: 6, alignItems: 'center', padding: '7px 10px',
                  background: !granted ? 'var(--surface-2)' : perm.access_level === 'read_only' ? '#fffbeb' : 'var(--surface)',
                  borderBottom: isLast ? 'none' : '1px solid var(--border)',
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={granted} onChange={() => togglePage(p.key)}
                      style={{ width: 13, height: 13, accentColor: 'var(--primary)', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '0.82rem', fontWeight: granted ? 600 : 400, color: granted ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {p.label}
                    </span>
                  </label>
                  {granted ? (
                    <select value={perm.access_level} onChange={e => updatePerm(p.key, 'access_level', e.target.value)}
                      style={{ fontSize: '0.74rem', padding: '3px 4px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--surface)', width: '100%', color: perm.access_level === 'read_only' ? '#92400e' : 'inherit' }}>
                      <option value="read_write">Read+Write</option>
                      <option value="read_only">Read Only</option>
                    </select>
                  ) : <span />}
                  {granted ? (
                    <select value={perm.visibility} onChange={e => updatePerm(p.key, 'visibility', e.target.value)}
                      style={{ fontSize: '0.74rem', padding: '3px 4px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--surface)', width: '100%' }}>
                      <option value="own">Own</option>
                      <option value="team">Team</option>
                      <option value="all">All</option>
                    </select>
                  ) : <span />}
                  {granted ? (
                    <input type="number" min="1" value={perm.expires_after_days ?? ''}
                      onChange={e => updatePerm(p.key, 'expires_after_days', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      placeholder="∞"
                      title="Days from now until access expires. Leave blank for permanent."
                      style={{ fontSize: '0.72rem', padding: '3px 4px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--surface)', width: '100%' }}
                    />
                  ) : <span />}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Access Control Panel ────────────────────────────────────────────────────
function AccessControlPanel({ targetUser, onSaved, readOnly, personas = [] }) {
  const [perms, setPerms] = useState({});   // { [pageKey]: { access_level, visibility, expires_at } }
  const [useCustom, setUseCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [applyPersonaId, setApplyPersonaId] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState('');

  useEffect(() => {
    if (!targetUser) return;
    setLoading(true);
    setMsg('');
    api.get(`/users/${targetUser.id}/permissions`)
      .then(({ data }) => {
        const rows = data.data || [];
        if (rows.length === 0) {
          setUseCustom(false);
          setPerms({});
        } else {
          setUseCustom(true);
          const map = {};
          for (const p of rows) {
            map[p.page] = {
              access_level: p.access_level || 'read_write',
              visibility:   p.visibility   || 'own',
              expires_at:   p.expires_at   ? p.expires_at.slice(0, 10) : '',
            };
          }
          setPerms(map);
        }
      })
      .catch(() => setMsg('Failed to load permissions.'))
      .finally(() => setLoading(false));
  }, [targetUser]);

  const toggleCustom = (on) => {
    setUseCustom(on);
    if (on) {
      const map = {};
      for (const key of defaultPagesForRole(targetUser.role)) {
        map[key] = { access_level: 'read_write', visibility: 'own', expires_at: '' };
      }
      setPerms(map);
    } else {
      setPerms({});
    }
  };

  const togglePage = (key) => {
    setPerms(prev => {
      const next = { ...prev };
      if (next[key]) { delete next[key]; }
      else { next[key] = { access_level: 'read_write', visibility: 'own', expires_at: '' }; }
      return next;
    });
  };

  const updatePerm = (key, field, value) =>
    setPerms(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const permissions = useCustom
        ? Object.entries(perms).map(([page, p]) => ({
            page,
            access_level: p.access_level,
            visibility:   p.visibility,
            expires_at:   p.expires_at || null,
          }))
        : [];
      await api.put(`/users/${targetUser.id}/permissions`, { permissions });
      setMsg('Access saved.');
      onSaved?.();
    } catch (err) {
      setMsg(err?.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const reloadPerms = () => {
    setLoading(true);
    api.get(`/users/${targetUser.id}/permissions`)
      .then(({ data }) => {
        const rows = data.data || [];
        if (rows.length === 0) { setUseCustom(false); setPerms({}); }
        else {
          setUseCustom(true);
          const map = {};
          for (const p of rows) {
            map[p.page] = { access_level: p.access_level || 'read_write', visibility: p.visibility || 'own', expires_at: p.expires_at ? p.expires_at.slice(0, 10) : '' };
          }
          setPerms(map);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleApplyPersona = async () => {
    if (!applyPersonaId) return;
    setApplying(true); setApplyMsg('');
    try {
      await api.post(`/personas/${applyPersonaId}/apply`, { user_ids: [targetUser.id] });
      setApplyMsg('Persona applied — permissions updated below.');
      setApplyPersonaId('');
      reloadPerms();
      onSaved?.();
    } catch (err) {
      setApplyMsg(err?.response?.data?.message || 'Failed to apply persona.');
    } finally { setApplying(false); }
  };

  if (!targetUser) return null;
  if (loading) return <div style={{ padding: '32px 0', textAlign: 'center' }}><div className="spinner" /></div>;

  return (
    <div>
      {/* Apply persona shortcut */}
      {personas.length > 0 && !readOnly && (
        <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--primary-lighter)', borderRadius: 8, border: '1px solid var(--primary-light)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary)', marginBottom: 8 }}>Apply Persona Template</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="form-control" value={applyPersonaId} onChange={e => setApplyPersonaId(e.target.value)} style={{ flex: 1, fontSize: '0.84rem' }}>
              <option value="">— Select a persona —</option>
              {personas.map(p => (
                <option key={p.id} value={p.id}>{roleEmoji[p.role] || '👤'} {p.name} ({roleLabel(p.role)})</option>
              ))}
            </select>
            <button className="btn btn-primary btn-sm" onClick={handleApplyPersona} disabled={applying || !applyPersonaId}>
              {applying ? 'Applying…' : 'Apply'}
            </button>
          </div>
          {applyMsg && (
            <div style={{ marginTop: 6, fontSize: '0.79rem', color: applyMsg.includes('updated') ? '#10b981' : 'var(--error)' }}>{applyMsg}</div>
          )}
          <div style={{ fontSize: '0.72rem', color: 'var(--primary)', marginTop: 4, opacity: 0.8 }}>
            Applying a persona replaces this user's current permissions entirely.
          </div>
        </div>
      )}
      {/* Custom toggle */}
      <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: readOnly ? 'default' : 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={useCustom} disabled={readOnly}
            onChange={e => toggleCustom(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: 'var(--primary)', marginTop: 2, flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>Custom page access</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
              {useCustom
                ? `Overriding role defaults — ${Object.keys(perms).length} page(s) configured`
                : `Using role defaults for ${ROLE_LABELS[targetUser.role] || targetUser.role}`}
            </div>
          </div>
        </label>
      </div>

      {/* Column headers when custom is on */}
      {useCustom && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 96px 76px 108px', gap: 6, padding: '0 10px', marginBottom: 4 }}>
          <span style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.6px' }}>Page</span>
          <span style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.6px' }}>Access</span>
          <span style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.6px' }}>Scope</span>
          <span style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.6px' }}>Expires</span>
        </div>
      )}

      {/* Page sections */}
      {PAGE_SECTIONS.map(sec => (
        <div key={sec.section} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-muted)', marginBottom: 5, paddingLeft: 10 }}>
            {sec.section}
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            {sec.pages.map((p, idx) => {
              const isDefault  = !p.defaultRoles || p.defaultRoles.includes(targetUser.role);
              const granted    = useCustom ? !!perms[p.key] : isDefault;
              const perm       = perms[p.key] || {};
              const isExpired  = perm.expires_at && new Date(perm.expires_at) < new Date();
              const isLast     = idx === sec.pages.length - 1;

              return (
                <div key={p.key} style={{
                  display: 'grid',
                  gridTemplateColumns: useCustom ? '1fr 96px 76px 108px' : '1fr auto',
                  gap: 6, alignItems: 'center',
                  padding: '7px 10px',
                  background: !granted ? 'var(--surface-2)' : isExpired ? '#fef2f2' : perm.access_level === 'read_only' ? '#fffbeb' : 'var(--surface)',
                  borderBottom: isLast ? 'none' : '1px solid var(--border)',
                }}>
                  {/* Page checkbox + label */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: useCustom && !readOnly ? 'pointer' : 'default', userSelect: 'none' }}>
                    <input type="checkbox" checked={granted}
                      disabled={!useCustom || readOnly}
                      onChange={() => togglePage(p.key)}
                      style={{ width: 13, height: 13, accentColor: 'var(--primary)', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: '0.82rem', fontWeight: granted ? 600 : 400, color: granted ? (isExpired ? '#dc2626' : 'var(--text-primary)') : 'var(--text-muted)' }}>
                      {p.label}
                    </span>
                    {!useCustom && isDefault && (
                      <span style={{ fontSize: '0.64rem', color: 'var(--text-muted)', marginLeft: 2 }}>default</span>
                    )}
                    {isExpired && (
                      <span style={{ fontSize: '0.64rem', color: '#dc2626', fontWeight: 600 }}>expired</span>
                    )}
                  </label>

                  {/* Access level */}
                  {useCustom && (
                    granted && !readOnly ? (
                      <select value={perm.access_level || 'read_write'} onChange={e => updatePerm(p.key, 'access_level', e.target.value)}
                        style={{ fontSize: '0.74rem', padding: '3px 4px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--surface)', width: '100%', color: perm.access_level === 'read_only' ? '#92400e' : 'inherit' }}>
                        <option value="read_write">Read+Write</option>
                        <option value="read_only">Read Only</option>
                      </select>
                    ) : granted ? (
                      <span style={{ fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4, background: perm.access_level === 'read_only' ? '#fef3c7' : '#d1fae5', color: perm.access_level === 'read_only' ? '#92400e' : '#065f46', fontWeight: 600, textAlign: 'center' }}>
                        {perm.access_level === 'read_only' ? 'Read Only' : 'R+W'}
                      </span>
                    ) : <span />
                  )}

                  {/* Visibility */}
                  {useCustom && (
                    granted && !readOnly ? (
                      <select value={perm.visibility || 'own'} onChange={e => updatePerm(p.key, 'visibility', e.target.value)}
                        style={{ fontSize: '0.74rem', padding: '3px 4px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--surface)', width: '100%' }}>
                        <option value="own">Own</option>
                        <option value="team">Team</option>
                        <option value="all">All</option>
                      </select>
                    ) : granted ? (
                      <span style={{ fontSize: '0.72rem', padding: '2px 6px', borderRadius: 4, background: 'var(--surface-2)', color: 'var(--text-secondary)', fontWeight: 500, textAlign: 'center' }}>
                        {perm.visibility || 'own'}
                      </span>
                    ) : <span />
                  )}

                  {/* Expires */}
                  {useCustom && (
                    granted && !readOnly ? (
                      <input type="date" value={perm.expires_at || ''} onChange={e => updatePerm(p.key, 'expires_at', e.target.value)}
                        title="Leave blank for no expiry"
                        style={{ fontSize: '0.72rem', padding: '3px 4px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--surface)', width: '100%' }}
                      />
                    ) : granted ? (
                      <span style={{ fontSize: '0.72rem', color: isExpired ? '#dc2626' : perm.expires_at ? '#d97706' : 'var(--text-muted)', fontWeight: perm.expires_at ? 600 : 400 }}>
                        {perm.expires_at ? new Date(perm.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                      </span>
                    ) : <span />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Access'}
          </button>
          {msg && (
            <span style={{ fontSize: '0.82rem', color: msg === 'Access saved.' ? '#10b981' : 'var(--error)' }}>{msg}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Apply Persona Modal ─────────────────────────────────────────────────────
function ApplyPersonaModal({ persona, users, onClose, onApplied }) {
  const [selected, setSelected] = useState([]);
  const [applying, setApplying] = useState(false);
  const [msg, setMsg] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');

  const toggle = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filtered = users.filter(u =>
    u.is_active &&
    (!roleFilter || u.role === roleFilter) &&
    (!search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()))
  );

  const handleApply = async () => {
    if (!selected.length) return;
    setApplying(true); setMsg('');
    try {
      await api.post(`/personas/${persona.id}/apply`, { user_ids: selected });
      setMsg(`Persona applied to ${selected.length} user(s) successfully.`);
      setSelected([]);
      onApplied?.();
    } catch (err) {
      setMsg(err?.response?.data?.message || 'Failed to apply persona.');
    } finally { setApplying(false); }
  };

  const isSuccess = msg.includes('successfully');

  return (
    <Modal open={true} onClose={onClose} title={`Apply "${persona.name}"`} size="md"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={handleApply} disabled={applying || !selected.length}>
          {applying ? 'Applying…' : `Apply to ${selected.length} user${selected.length !== 1 ? 's' : ''}`}
        </button>
      </>}
    >
      <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: '0.84rem', color: '#92400e' }}>
        Applying this persona <strong>replaces</strong> the selected users' current page permissions entirely.
      </div>
      {msg && (
        <div className={`alert alert-${isSuccess ? 'success' : 'error'}`} style={{ marginBottom: 14 }}>{msg}</div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input className="form-control" placeholder="Search by name or email…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
        <select className="form-control" style={{ width: 140 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
        </select>
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 300, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.84rem' }}>No users found.</div>
        ) : filtered.map((u, idx) => {
          const checked = selected.includes(u.id);
          return (
            <label key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer',
              background: checked ? 'var(--primary-lighter)' : 'none', userSelect: 'none',
              borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <input type="checkbox" checked={checked} onChange={() => toggle(u.id)}
                style={{ width: 14, height: 14, accentColor: 'var(--primary)', flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', color: checked ? 'var(--primary)' : 'var(--text-primary)' }}>{u.name}</div>
                <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)' }}>{u.email}</div>
              </div>
              <span className={`badge badge-${roleColors[u.role] || 'neutral'}`} style={{ textTransform: 'capitalize', flexShrink: 0 }}>
                {roleLabel(u.role)}
              </span>
            </label>
          );
        })}
      </div>
      {filtered.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(filtered.map(u => u.id))}>Select all</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected([])}>Clear</button>
        </div>
      )}
    </Modal>
  );
}

// ─── Roles Panel ─────────────────────────────────────────────────────────────
function RolesPanel({ targetUser, currentUser, onUpdated }) {
  const [roleRows, setRoleRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [newRole, setNewRole] = useState('');
  const [newExpiry, setNewExpiry] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/users/${targetUser.id}/roles`)
      .then(({ data }) => setRoleRows(data.data || []))
      .catch(() => setMsg('Failed to load roles.'))
      .finally(() => setLoading(false));
  }, [targetUser.id]);

  useEffect(() => { load(); }, [load]);

  const addRole = () => {
    if (!newRole) return;
    if (roleRows.some(r => r.role === newRole)) { setMsg(`${roleLabel(newRole)} is already assigned.`); return; }
    setRoleRows(prev => [...prev, { role: newRole, expires_at: newExpiry || null, isNew: true }]);
    setNewRole(''); setNewExpiry(''); setMsg('');
  };

  const removeRole = (role) => setRoleRows(prev => prev.filter(r => r.role !== role));

  const handleSave = async () => {
    if (!roleRows.length) { setMsg('At least one role is required.'); return; }
    setSaving(true); setMsg('');
    try {
      await api.put(`/users/${targetUser.id}/roles`, {
        roles: roleRows.map(r => ({ role: r.role, expires_at: r.expires_at || null }))
      });
      setMsg('Roles saved.');
      load();
      onUpdated?.();
    } catch (err) {
      setMsg(err?.response?.data?.message || 'Failed to save roles.');
    } finally { setSaving(false); }
  };

  const isAdminView = (currentUser?.roles || [currentUser?.role]).includes('admin');

  if (loading) return <div style={{ padding: '32px 0', textAlign: 'center' }}><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ marginBottom: 16, padding: '12px 14px', background: 'var(--primary-lighter)', borderRadius: 8, border: '1px solid var(--primary-light)', fontSize: '0.8rem', color: 'var(--primary)' }}>
        The user's effective access is the <strong>union</strong> of all active (non-expired) roles. Expired roles are automatically excluded from permission checks.
      </div>

      {/* Assigned roles list */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        {roleRows.length === 0 ? (
          <div style={{ padding: '14px 16px', color: 'var(--text-muted)', fontSize: '0.84rem', textAlign: 'center' }}>No roles assigned yet.</div>
        ) : roleRows.map((r, idx) => {
          const isExpired = r.expires_at && new Date(r.expires_at) < new Date();
          return (
            <div key={r.role} style={{
              display: 'grid', gridTemplateColumns: '28px 1fr 1fr auto',
              gap: 8, alignItems: 'center', padding: '8px 12px',
              background: isExpired ? '#fef2f2' : r.isNew ? 'var(--primary-lighter)' : 'var(--surface)',
              borderBottom: idx < roleRows.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <span style={{ fontSize: '1.1rem' }}>{roleEmoji[r.role] || '👤'}</span>
              <div>
                <span className={`badge badge-${roleColors[r.role] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>
                  {roleLabel(r.role)}
                </span>
                {r.isNew && <span style={{ fontSize: '0.67rem', color: 'var(--primary)', marginLeft: 6 }}>new</span>}
              </div>
              <div style={{ fontSize: '0.78rem', color: isExpired ? '#dc2626' : r.expires_at ? '#d97706' : 'var(--text-muted)' }}>
                {r.expires_at ? (
                  isAdminView
                    ? <input type="date" value={r.expires_at?.slice(0,10) || ''} min={new Date().toISOString().slice(0,10)}
                        onChange={e => setRoleRows(prev => prev.map(x => x.role === r.role ? { ...x, expires_at: e.target.value || null } : x))}
                        style={{ fontSize: '0.75rem', padding: '2px 4px', borderRadius: 5, border: '1px solid var(--border)', width: 120 }}
                      />
                    : <>Expires: {new Date(r.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}</>
                ) : 'Permanent'}
              </div>
              {isAdminView && (
                <button onClick={() => removeRole(r.role)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '1rem', padding: '2px 4px', borderRadius: 4 }}
                  title="Remove role">×</button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add role */}
      {isAdminView && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
          <select className="form-control" value={newRole} onChange={e => setNewRole(e.target.value)} style={{ flex: 1, fontSize: '0.84rem' }}>
            <option value="">— Add a role —</option>
            {ROLES.filter(r => !roleRows.some(x => x.role === r)).map(r => (
              <option key={r} value={r}>{roleLabel(r)}</option>
            ))}
          </select>
          <input type="date" className="form-control" value={newExpiry} onChange={e => setNewExpiry(e.target.value)}
            min={new Date().toISOString().slice(0,10)} title="Optional: role expires on this date"
            style={{ width: 140, fontSize: '0.84rem' }} placeholder="Expiry (opt.)" />
          <button className="btn btn-secondary btn-sm" onClick={addRole} disabled={!newRole}>Add</button>
        </div>
      )}

      {isAdminView && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Roles'}
          </button>
          {msg && (
            <span style={{ fontSize: '0.82rem', color: msg === 'Roles saved.' ? '#10b981' : 'var(--error)' }}>{msg}</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── User Detail Drawer ──────────────────────────────────────────────────────
function UserDrawer({ user: targetUser, currentUser, onClose, onUpdated, allUsers, territories = [], personas = [] }) {
  const currentUserRoles = currentUser?.roles || (currentUser?.role ? [currentUser.role] : []);
  const isAdmin = currentUserRoles.includes('admin');
  const isManager = currentUserRoles.includes('manager');
  const [tab, setTab] = useState('profile');
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    if (!targetUser) return;
    setForm({
      name: targetUser.name || '',
      email: targetUser.email || '',
      password: '',
      role: targetUser.role || 'mr',
      phone: targetUser.phone || '',
      username: targetUser.username || '',
      territory: targetUser.territory || '',
      department: targetUser.department || '',
      reports_to: targetUser.reports_to || '',
    });
    setMsg({ type: '', text: '' });
    setTab('profile');
  }, [targetUser]);

  if (!targetUser) return null;

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg({ type: '', text: '' });
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      // Managers can only change department
      if (isManager && !isAdmin) {
        const { department } = payload;
        await api.put(`/users/${targetUser.id}`, { department, allowed_pages: targetUser.allowed_pages });
      } else {
        await api.put(`/users/${targetUser.id}`, payload);
      }
      setMsg({ type: 'success', text: 'Saved successfully.' });
      onUpdated();
    } catch (err) {
      setMsg({ type: 'error', text: err?.response?.data?.message || 'Failed to save.' });
    } finally { setSaving(false); }
  };

  const [fg] = getAvatarColors(targetUser.name);
  const initials = targetUser.name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex' }}>
      <div style={{ flex: 1, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      <div style={{ width: 540, background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, flexShrink: 0,
            background: `linear-gradient(135deg, ${fg}22, ${fg}44)`, border: `2px solid ${fg}33`,
            color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: '1rem',
          }}>{initials}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{targetUser.name}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{targetUser.email}</div>
            <span className={`badge badge-${roleColors[targetUser.role] || 'neutral'}`} style={{ textTransform: 'capitalize', marginTop: 4 }}>{roleLabel(targetUser.role)}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: 'var(--text-muted)', fontSize: '1.3rem', lineHeight: 1 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)' }}>
          {['profile', 'roles', 'access'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: tab === t ? 700 : 500,
              color: tab === t ? 'var(--primary)' : 'var(--text-secondary)',
              borderBottom: tab === t ? '2px solid var(--primary)' : '2px solid transparent',
              marginBottom: -1, textTransform: 'capitalize',
            }}>{t === 'access' ? 'Access Control' : t === 'roles' ? 'Roles' : 'Profile'}</button>
          ))}
        </div>

        <div style={{ padding: '20px 24px', flex: 1 }}>
          {!targetUser.is_active && (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.875rem' }}>⛔ Account Frozen</div>
              <div style={{ color: '#991b1b', fontSize: '0.8rem', marginTop: 2 }}>This account is frozen and cannot be edited. Unfreeze to make changes.</div>
            </div>
          )}
          {tab === 'profile' && (
            <form onSubmit={handleSave}>
              {msg.text && <div className={`alert alert-${msg.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 14 }}>{msg.text}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} disabled={isManager && !isAdmin} />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} disabled={isManager && !isAdmin} />
                </div>
              </div>

              {isAdmin && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Role</label>
                      <select className="form-control" value={form.role} onChange={e => set('role', e.target.value)}>
                        {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">New Password</label>
                      <input className="form-control" type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Leave blank to keep" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Username <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>(for login)</span></label>
                      <input className="form-control" placeholder="e.g. rahul.bd" value={form.username || ''} onChange={e => set('username', e.target.value)} />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Territory</label>
                      <select className="form-control" value={form.territory} onChange={e => set('territory', e.target.value)}>
                        <option value="">— None —</option>
                        {territories.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <select className="form-control" value={form.department} onChange={e => set('department', e.target.value)}>
                        <option value="">— None —</option>
                        {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Reports To</label>
                      <select className="form-control" value={form.reports_to} onChange={e => set('reports_to', e.target.value)}>
                        <option value="">— None —</option>
                        {allUsers.filter(u => u.is_active).map(m => <option key={m.id} value={m.id}>{m.name} ({roleLabel(m.role)})</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Manager can only edit department */}
              {isManager && !isAdmin && (
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <select className="form-control" value={form.department} onChange={e => set('department', e.target.value)}>
                    <option value="">— None —</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          )}

          {tab === 'roles' && (
            <RolesPanel
              targetUser={targetUser}
              currentUser={currentUser}
              onUpdated={onUpdated}
            />
          )}

          {tab === 'access' && (
            <AccessControlPanel
              targetUser={targetUser}
              onSaved={onUpdated}
              readOnly={false}
              personas={personas}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Users Page ──────────────────────────────────────────────────────────
export default function Users() {
  const { user: currentUser, isAdmin, isManager } = useAuth();

  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'personas'

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null); // selected user

  // Personas
  const [personas, setPersonas] = useState([]);
  const [personasLoading, setPersonasLoading] = useState(false);
  const [personaModal, setPersonaModal] = useState(false);
  const [editPersona, setEditPersona] = useState(null); // null = create
  const [personaForm, setPersonaForm] = useState(defaultPersonaForm);
  const [personaPermissions, setPersonaPermissions] = useState([]); // granular page permissions for persona
  const [personaUseCustom, setPersonaUseCustom] = useState(false);
  const [personaPermsLoading, setPersonaPermsLoading] = useState(false);
  const [personaSaving, setPersonaSaving] = useState(false);
  const [personaError, setPersonaError] = useState('');
  const [personaDeleteConfirm, setPersonaDeleteConfirm] = useState(null);
  const [applyPersonaModal, setApplyPersonaModal] = useState(null); // persona to apply
  const [territories, setTerritories] = useState([]);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users', { params: { role: roleFilter || undefined, search: debouncedSearch || undefined } });
      setUsers(data.data || []);
    } finally { setLoading(false); }
  }, [debouncedSearch, roleFilter]);

  useEffect(() => { load(); }, [load]);

  // When drawer user data changes after save, refresh drawer with latest
  const handleUpdated = async () => {
    await load();
    if (drawer) {
      const { data } = await api.get(`/users/${drawer.id}`).catch(() => ({ data: { data: null } }));
      if (data?.data) setDrawer(data.data);
    }
  };

  const handleAddSave = async () => {
    if (!form.employee_id) { setAddError('Please select an employee.'); return; }
    if (!form.role || !form.password) { setAddError('Role and password are required.'); return; }
    setSaving(true); setAddError('');
    try {
      await api.post('/users', form);
      setAddModal(false);
      load();
      // Refresh available employees list
      const { data } = await api.get('/hr/without-user');
      setAvailableEmployees(data.data || []);
    } catch (err) {
      const d = err.response?.data;
      const msg = d?.message || (Array.isArray(d?.errors) ? d.errors.map(e => e.msg).join(', ') : null) || 'Failed to save user.';
      setAddError(msg);
    }
    finally { setSaving(false); }
  };

  const handleToggle = async (u) => {
    await api.patch(`/users/${u.id}/toggle-status`); load();
  };

  const handleDelete = async () => {
    const id = deleteConfirm.id;
    await api.delete(`/users/${id}`);
    setDeleteConfirm(null);
    if (drawer?.id === id) setDrawer(null);
    load();
  };

  const handleUnfreeze = async (id) => {
    try { await api.patch(`/users/${id}/unfreeze`); load(); if (drawer?.id === id) setDrawer(null); }
    catch (err) { console.error('Unfreeze failed', err); }
  };

  // ── Persona helpers ──
  const loadPersonas = useCallback(async () => {
    setPersonasLoading(true);
    try {
      const { data } = await api.get('/personas');
      setPersonas(data.data || []);
    } finally { setPersonasLoading(false); }
  }, []);

  useEffect(() => { loadPersonas(); }, [loadPersonas]);
  useEffect(() => { api.get('/territories').then(r => setTerritories(r.data?.data || r.data || [])).catch(() => { }); }, []);

  const openCreatePersona = () => {
    setEditPersona(null);
    setPersonaForm(defaultPersonaForm);
    setPersonaPermissions([]);
    setPersonaUseCustom(false);
    setPersonaError('');
    setPersonaModal(true);
  };

  const openEditPersona = async (p) => {
    setEditPersona(p);
    // p.roles comes from backend as array (already parsed); fallback to [p.role]
    const existingRoles = Array.isArray(p.roles) && p.roles.length ? p.roles : (p.role ? [p.role] : ['mr']);
    const primaryRole = existingRoles[0];
    const additionalRoles = existingRoles.slice(1);
    setPersonaForm({
      name: p.name || '',
      description: p.description || '',
      role: primaryRole,
      roles: additionalRoles,
      department: p.department || '',
      territory: p.territory || '',
    });
    setPersonaPermissions([]);
    setPersonaUseCustom(false);
    setPersonaError('');
    setPersonaModal(true);
    // Load existing granular permissions
    setPersonaPermsLoading(true);
    try {
      const { data } = await api.get(`/personas/${p.id}/permissions`);
      const rows = data.data || [];
      setPersonaPermissions(rows.map(r => ({
        page: r.page,
        access_level: r.access_level || 'read_write',
        visibility: r.visibility || 'own',
        expires_after_days: r.expires_after_days ?? null,
      })));
      setPersonaUseCustom(rows.length > 0);
    } catch { /* non-fatal — leave empty */ }
    finally { setPersonaPermsLoading(false); }
  };

  const handlePersonaSave = async () => {
    if (!personaForm.name) { setPersonaError('Persona name is required.'); return; }
    if (!personaForm.role) { setPersonaError('At least one role is required.'); return; }
    setPersonaSaving(true); setPersonaError('');
    try {
      // Build deduplicated roles array: primary first, then additional
      const additionalRoles = (personaForm.roles || []).filter(r => r && r !== personaForm.role);
      const allRoles = [personaForm.role, ...additionalRoles];
      const payload = {
        ...personaForm,
        role: personaForm.role,
        roles: allRoles,
      };
      delete payload.allowed_pages; // managed via permissions endpoint separately
      let savedId;
      if (editPersona) {
        await api.put(`/personas/${editPersona.id}`, payload);
        savedId = editPersona.id;
      } else {
        const { data } = await api.post('/personas', payload);
        savedId = data.data.id;
      }
      // Save granular page permissions
      const permissions = personaUseCustom ? personaPermissions : [];
      await api.put(`/personas/${savedId}/permissions`, { permissions });
      setPersonaModal(false);
      loadPersonas();
    } catch (err) { setPersonaError(err.response?.data?.message || 'Failed to save.'); }
    finally { setPersonaSaving(false); }
  };

  const handlePersonaDelete = async () => {
    await api.delete(`/personas/${personaDeleteConfirm.id}`);
    setPersonaDeleteConfirm(null);
    loadPersonas();
  };

  const applyPersona = (persona) => {
    if (!persona) { setForm(defaultForm); return; }
    setForm(p => ({
      ...p,
      role: persona.role || p.role,
      department: persona.department || p.department,
      territory: persona.territory || p.territory,
      allowed_pages: persona.allowed_pages || null,
    }));
  };

  const pf = (k, v) => setPersonaForm(p => ({ ...p, [k]: v }));

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const managers = users.filter(u => u.is_active); // Show all active users

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">
            {isAdmin ? 'Manage all users, roles, and page access' : 'View and manage your team members'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isAdmin && activeTab === 'personas' && (
            <button className="btn btn-secondary" onClick={openCreatePersona}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              New Persona
            </button>
          )}
          {isAdmin && activeTab === 'users' && (
            <button className="btn btn-primary" onClick={async () => {
              setForm(defaultForm); setAddError(''); setEmpSearch(''); setAddModal(true);
              if (!personas.length) loadPersonas();
              try { const { data } = await api.get('/hr/without-user'); setAvailableEmployees(data.data || []); } catch { }
            }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Add User
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {[{ key: 'users', label: 'Users' }, { key: 'personas', label: 'Persona Templates' }].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '10px 24px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '0.875rem', fontWeight: activeTab === t.key ? 700 : 500,
            color: activeTab === t.key ? 'var(--primary)' : 'var(--text-secondary)',
            borderBottom: activeTab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Users Tab ── */}
      {activeTab === 'users' && (<>
        {/* Filters */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ padding: '14px 16px' }}>
            <div className="search-bar">
              <div className="search-input-wrap">
                <span className="search-icon"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg></span>
                <input className="form-control" placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <select className="form-control form-select" style={{ width: 150 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="">All Roles</option>
                {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* User Cards */}
        {loading ? (
          <div className="loading-overlay"><div className="spinner spinner-lg" /></div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
            {users.length === 0 && (
              <div className="card"><div className="card-body"><div className="empty-state">
                <p>{isManager ? 'No team members found.' : 'No users found.'}</p>
              </div></div></div>
            )}
            {users.sort((a, b) => a.name.localeCompare(b.name)).map(u => {
              const [fg] = getAvatarColors(u.name);
              const initials = u.name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
              const hasCustomAccess = u.allowed_pages && Array.isArray(u.allowed_pages) && u.allowed_pages.length > 0;
              return (
                <div key={u.id} className="card" style={{ opacity: u.is_active ? 1 : 0.65 }}>
                  <div className="card-body">
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <div style={{
                        width: 52, height: 52, borderRadius: 14, flexShrink: 0,
                        background: `linear-gradient(135deg, ${fg}22, ${fg}44)`,
                        border: `2px solid ${fg}33`, color: fg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, fontSize: '1rem',
                      }}>{initials}</div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{u.name}</span>
                          <span className={`badge badge-${roleColors[u.role] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{roleLabel(u.role)}</span>
                          {!u.is_active && <span className="badge badge-neutral">Frozen</span>}
                          {hasCustomAccess && (
                            <span className="badge badge-info" title="Has custom page access overrides">Custom Access</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>{u.email}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {u.department && <span style={{ marginRight: 8 }}>{u.department}</span>}
                          {u.territory && <span>📍 {u.territory}</span>}
                        </div>
                        {u.last_login && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                            Last login: {new Date(u.last_login).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => setDrawer(u)}>
                        {isAdmin ? 'Edit / Access' : 'Edit Dept / Access'}
                      </button>
                      {isAdmin && (
                        <>
                          {!u.is_active
                            ? <button className="btn btn-success btn-sm" onClick={() => handleUnfreeze(u.id)}>Unfreeze</button>
                            : <>
                              <button className={`btn btn-sm ${u.is_active ? 'btn-ghost' : 'btn-success'}`}
                                style={{ color: u.is_active ? 'var(--warning)' : undefined }}
                                onClick={() => handleToggle(u)}>
                                {u.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(u)}>Freeze</button>
                            </>
                          }
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>)}

      {/* ── Personas Tab ── */}
      {activeTab === 'personas' && (
        personasLoading ? (
          <div className="loading-overlay"><div className="spinner spinner-lg" /></div>
        ) : (
          <div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 18 }}>
              Persona templates let you predefine role, department, territory, and page access — apply them when creating new users to speed up onboarding.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {personas.length === 0 && (
                <div className="card"><div className="card-body"><div className="empty-state">
                  <p>No personas yet. {isAdmin && 'Create one to get started.'}</p>
                </div></div></div>
              )}
              {personas.map(p => {
                const pageCount = p.allowed_pages ? (Array.isArray(p.allowed_pages) ? p.allowed_pages.length : 0) : defaultPagesForRole(p.role).length;
                return (
                  <div key={p.id} className="card">
                    <div className="card-body">
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                        <div style={{
                          width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                          background: 'var(--primary-lighter)', border: '2px solid var(--primary-light)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem',
                        }}>{(Array.isArray(p.roles) && p.roles.length > 1) ? '🎭' : (roleEmoji[p.role] || '👤')}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{p.name}</div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                            {(Array.isArray(p.roles) && p.roles.length ? p.roles : [p.role]).map(r => (
                              <span key={r} className={`badge badge-${roleColors[r] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{roleLabel(r)}</span>
                            ))}
                            {p.allowed_pages && <span className="badge badge-info">Custom Access</span>}
                          </div>
                          {p.description && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 5 }}>{p.description}</div>
                          )}
                          <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 6, display: 'flex', gap: 12 }}>
                            {p.department && <span>🏢 {p.department}</span>}
                            {p.territory && <span>📍 {p.territory}</span>}
                            <span>🔐 {pageCount} pages</span>
                          </div>
                          {p.created_by_name && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>Created by {p.created_by_name}</div>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button className="btn btn-primary btn-sm" onClick={() => setApplyPersonaModal(p)}>
                          Apply to users…
                        </button>
                        {isAdmin && (
                          <>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEditPersona(p)}>Edit</button>
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => setPersonaDeleteConfirm(p)}>Delete</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}

      {/* User Drawer (Edit + Access Control) */}
      {drawer && (
        <UserDrawer
          user={drawer}
          currentUser={currentUser}
          allUsers={users}
          territories={territories}
          personas={personas}
          onClose={() => setDrawer(null)}
          onUpdated={handleUpdated}
        />
      )}

      {/* Apply Persona Modal */}
      {applyPersonaModal && (
        <ApplyPersonaModal
          persona={applyPersonaModal}
          users={users}
          onClose={() => setApplyPersonaModal(null)}
          onApplied={() => { load(); }}
        />
      )}

      {/* Add User Modal (admin only) */}
      <Modal open={addModal} onClose={() => setAddModal(false)} title="Add User" size="md"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAddModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAddSave} disabled={saving}>{saving ? 'Saving…' : 'Add User'}</button>
        </>}
      >
        {addError && <div className="alert alert-error" style={{ marginBottom: 14 }}>{addError}</div>}

        {/* ── Step 1: Select Employee ── */}
        <div style={{ marginBottom: 16 }}>
          <label className="form-label" style={{ fontWeight: 700 }}>
            Select Employee *
            <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-muted)' }}>
              Only active employees without a login account are listed
            </span>
          </label>
          {availableEmployees.length === 0 ? (
            <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: '0.84rem', color: '#92400e' }}>
              All active employees already have user accounts, or no employees exist yet.
              <a href="/admin/hr" style={{ marginLeft: 6, color: 'var(--primary)', fontWeight: 600 }}>Add employees in HR →</a>
            </div>
          ) : (
            <>
              <input
                className="form-control"
                placeholder="Search by name, code, designation…"
                value={empSearch}
                onChange={e => setEmpSearch(e.target.value)}
                style={{ marginBottom: 6 }}
              />
              {(() => {
                const q = empSearch.toLowerCase();
                const filtered = availableEmployees.filter(e =>
                  !q || `${e.first_name} ${e.last_name}`.toLowerCase().includes(q)
                  || (e.employee_code || '').toLowerCase().includes(q)
                  || (e.designation || '').toLowerCase().includes(q)
                  || (e.department || '').toLowerCase().includes(q)
                );
                return (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 220, overflowY: 'auto' }}>
                    {filtered.length === 0 ? (
                      <div style={{ padding: '10px 14px', fontSize: '0.83rem', color: 'var(--text-muted)' }}>No matching employees.</div>
                    ) : filtered.map(emp => {
                      const selected = String(form.employee_id) === String(emp.id);
                      return (
                        <button key={emp.id} type="button"
                          onClick={() => {
                            f('employee_id', emp.id);
                            f('name', `${emp.first_name}${emp.last_name ? ' ' + emp.last_name : ''}`);
                            f('email', emp.email || '');
                            f('phone', emp.phone || '');
                            f('department', emp.department || '');
                          }}
                          style={{
                            width: '100%', textAlign: 'left', padding: '9px 14px',
                            background: selected ? 'var(--primary-lighter)' : 'none',
                            border: 'none', borderBottom: '1px solid var(--border)',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                          }}
                          onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'var(--surface-2)'; }}
                          onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'none'; }}
                        >
                          <div style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, background: selected ? 'var(--primary)' : 'var(--surface-2)', color: selected ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>
                            {(emp.first_name?.[0] || '?').toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.88rem', color: selected ? 'var(--primary)' : 'var(--text-primary)' }}>
                              {emp.first_name} {emp.last_name}
                            </div>
                            <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: 1 }}>
                              {emp.employee_code}{emp.designation ? ` · ${emp.designation}` : ''}{emp.department ? ` · ${emp.department}` : ''}
                            </div>
                          </div>
                          {selected && (
                            <svg width="16" height="16" fill="none" stroke="var(--primary)" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* ── Auto-filled info (read-only) ── */}
        {form.employee_id && (
          <div style={{ marginBottom: 14, padding: '10px 14px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Auto-filled from employee record</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: '0.83rem' }}>
              <div><span style={{ color: 'var(--text-muted)' }}>Name: </span><strong>{form.name}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Email: </span><strong>{form.email || '—'}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Phone: </span><strong>{form.phone || '—'}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Department: </span><strong>{form.department || '—'}</strong></div>
            </div>
          </div>
        )}

        {/* ── Step 2: Login settings ── */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Password *</label>
            <input className="form-control" type="password" value={form.password} onChange={e => f('password', e.target.value)} placeholder="Min 8 chars, uppercase, number, special" />
          </div>
          <div className="form-group">
            <label className="form-label">Username <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>(optional, for login)</span></label>
            <input className="form-control" placeholder="e.g. rahul.bd" value={form.username || ''} onChange={e => f('username', e.target.value)} />
          </div>
        </div>

        {/* ── Step 3: Role & access ── */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Role *</label>
            <select className="form-control" value={form.role} onChange={e => f('role', e.target.value)}>
              {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Territory</label>
            <select className="form-control" value={form.territory} onChange={e => f('territory', e.target.value)}>
              <option value="">— None —</option>
              {territories.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Reports To</label>
            <select className="form-control" value={form.reports_to} onChange={e => f('reports_to', e.target.value)}>
              <option value="">— None —</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name} ({roleLabel(m.role)})</option>)}
            </select>
          </div>
        </div>

        {/* Persona selector */}
        {personas.length > 0 && (
          <div className="form-group" style={{ marginTop: 4, padding: '10px 14px', background: 'var(--primary-lighter)', borderRadius: 8, border: '1px solid var(--primary-light)' }}>
            <label className="form-label" style={{ color: 'var(--primary)', fontWeight: 700, marginBottom: 4 }}>Apply Persona Template</label>
            <select className="form-control" defaultValue=""
              onChange={e => applyPersona(personas.find(p => p.id === +e.target.value) || null)}
            >
              <option value="">— No template —</option>
              {personas.map(p => <option key={p.id} value={p.id}>{roleEmoji[p.role]} {p.name} ({roleLabel(p.role)})</option>)}
            </select>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Pre-fills role, department, and territory</div>
          </div>
        )}
      </Modal>

      {/* Freeze Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Freeze User"
        footer={<><button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={handleDelete}>Freeze</button></>}
      >
        <p>Are you sure you want to freeze <strong>{deleteConfirm?.name}</strong>? The account will be locked. You can unfreeze it later.</p>
      </Modal>

      {/* Create / Edit Persona Modal */}
      <Modal open={personaModal} onClose={() => setPersonaModal(false)}
        title={editPersona ? 'Edit Persona' : 'New Persona Template'}
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setPersonaModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handlePersonaSave} disabled={personaSaving}>{personaSaving ? 'Saving…' : editPersona ? 'Save Changes' : 'Create Persona'}</button>
        </>}
      >
        {personaError && <div className="alert alert-error" style={{ marginBottom: 14 }}>{personaError}</div>}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Persona Name *</label>
            <input className="form-control" placeholder="e.g. Field Medical Rep" value={personaForm.name} onChange={e => pf('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Primary Role *</label>
            <select className="form-control" value={personaForm.role} onChange={e => {
              const newPrimary = e.target.value;
              // Remove new primary from additional roles to avoid duplicates
              const filtered = (personaForm.roles || []).filter(r => r !== newPrimary);
              pf('role', newPrimary);
              pf('roles', filtered);
              // Reset page defaults using union of all roles
              if (personaUseCustom) {
                const allRoles = [newPrimary, ...filtered];
                setPersonaPermissions(defaultPagesForRoles(allRoles).map(page => ({
                  page, access_level: 'read_write', visibility: 'own', expires_after_days: null,
                })));
              }
            }}>
              {ROLES.map(r => <option key={r} value={r}>{roleEmoji[r]} {roleLabel(r)}</option>)}
            </select>
          </div>
        </div>

        {/* Additional Roles */}
        <div className="form-group" style={{ marginTop: -4 }}>
          <label className="form-label" style={{ marginBottom: 6 }}>
            Additional Roles
            <span style={{ fontSize: '0.72rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
              Access = union of all selected roles
            </span>
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ROLES.filter(r => r !== personaForm.role).map(r => {
              const checked = (personaForm.roles || []).includes(r);
              return (
                <label key={r} style={{
                  display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                  padding: '4px 10px', borderRadius: 20,
                  border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
                  background: checked ? 'var(--primary-lighter)' : 'var(--surface-2)',
                  fontSize: '0.78rem', fontWeight: checked ? 600 : 400,
                  color: checked ? 'var(--primary)' : 'var(--text-secondary)',
                  userSelect: 'none', transition: 'all 0.15s',
                }}>
                  <input type="checkbox" checked={checked} style={{ display: 'none' }}
                    onChange={e => {
                      const next = e.target.checked
                        ? [...(personaForm.roles || []), r]
                        : (personaForm.roles || []).filter(x => x !== r);
                      pf('roles', next);
                      // Regenerate page defaults with new role set
                      if (personaUseCustom) {
                        const allRoles = [personaForm.role, ...next];
                        setPersonaPermissions(defaultPagesForRoles(allRoles).map(page => ({
                          page, access_level: 'read_write', visibility: 'own', expires_after_days: null,
                        })));
                      }
                    }}
                  />
                  {roleEmoji[r]} {roleLabel(r)}
                </label>
              );
            })}
          </div>
          {(personaForm.roles || []).length > 0 && (
            <div style={{ marginTop: 6, fontSize: '0.74rem', color: 'var(--primary)', fontWeight: 500 }}>
              {[personaForm.role, ...(personaForm.roles || [])].map(r => roleLabel(r)).join(' + ')} access will be granted when applied
            </div>
          )}
        </div>

        <div className="form-row" style={{ marginTop: 8 }}>
          <div className="form-group">
            <label className="form-label">Department</label>
            <select className="form-control" value={personaForm.department} onChange={e => pf('department', e.target.value)}>
              <option value="">— None —</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Territory</label>
            <select className="form-control" value={personaForm.territory} onChange={e => pf('territory', e.target.value)}>
              <option value="">— None —</option>
              {territories.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <input className="form-control" placeholder="Brief description of this persona's responsibilities…" value={personaForm.description} onChange={e => pf('description', e.target.value)} />
        </div>

        <div style={{ marginTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: 'var(--text-muted)' }}>Page Access</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={personaUseCustom}
                onChange={e => {
                  setPersonaUseCustom(e.target.checked);
                  if (e.target.checked && personaPermissions.length === 0) {
                    const allRoles = [personaForm.role, ...(personaForm.roles || [])];
                    setPersonaPermissions(defaultPagesForRoles(allRoles).map(page => ({
                      page, access_level: 'read_write', visibility: 'own', expires_after_days: null,
                    })));
                  }
                }}
                style={{ width: 14, height: 14, accentColor: 'var(--primary)' }}
              />
              <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Custom permissions</span>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                {personaUseCustom ? '(access_level, scope & expiry per page)' : '(role defaults when applied)'}
              </span>
            </label>
          </div>
          {personaPermsLoading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}><div className="spinner" /></div>
          ) : personaUseCustom ? (
            <PersonaPermissionEditor
              permissions={personaPermissions}
              onChange={setPersonaPermissions}
            />
          ) : (
            <div style={{ padding: '12px 14px', background: 'var(--surface-2)', borderRadius: 8, fontSize: '0.83rem', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
              No custom page access defined. When applied, users will fall back to their role's default page access.
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Persona Confirm */}
      <Modal open={!!personaDeleteConfirm} onClose={() => setPersonaDeleteConfirm(null)} title="Delete Persona"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setPersonaDeleteConfirm(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={handlePersonaDelete}>Delete</button>
        </>}
      >
        <p>Delete persona <strong>{personaDeleteConfirm?.name}</strong>? This cannot be undone. Existing users are not affected.</p>
      </Modal>
    </div>
  );
}
